import maplibregl from 'maplibre-gl'
import { Protocol, PMTiles } from 'pmtiles'
import { Capacitor } from '@capacitor/core'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { CACHED_GLYPHS } from '../tile-cache/vector-style-cache-service.js'

const TILE_ORIGIN = Capacitor.isNativePlatform() ? 'https://taselith.com' : ''
const PLSS_URL = `${TILE_ORIGIN}/data/land/plss.pmtiles`

const SOURCE_ID = 'plss-source'
const TOWNSHIP_FILL_LAYER = 'plss-township-fill'
const TOWNSHIP_LINE_LAYER = 'plss-township-lines'
const SECTION_LINE_LAYER = 'plss-section-lines'
const TOWNSHIP_LABEL_LAYER = 'plss-township-labels'
const SECTION_LABEL_LAYER = 'plss-section-labels'
const DEFAULT_OPACITY = 0.4

class PlssService {
  private map: maplibregl.Map | null = null
  private protocol: Protocol | null = null
  private _enabled = false
  private _opacity = DEFAULT_OPACITY

  get enabled(): boolean {
    return this._enabled
  }
  get opacity(): number {
    return this._opacity
  }

  register(map: maplibregl.Map, existingProtocol?: Protocol): void {
    this.map = map
    if (existingProtocol) {
      this.protocol = existingProtocol
      this.protocol.add(new PMTiles(PLSS_URL))
    } else {
      this.protocol = new Protocol()
      this.protocol.add(new PMTiles(PLSS_URL))
      maplibregl.addProtocol('pmtiles', this.protocol.tile)
    }
  }

  unregister(): void {
    this.disable()
    this.protocol = null
    this.map = null
  }

  enable(): void {
    if (!this.map || this._enabled) return
    this._enabled = true

    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'vector',
        url: `pmtiles://${PLSS_URL}`,
        minzoom: 7,
        maxzoom: 12,
      })
    }

    this.ensureGlyphs()

    // Township boundaries - visible at lower zooms
    if (!this.map.getLayer(TOWNSHIP_LINE_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: TOWNSHIP_LINE_LAYER,
          type: 'line',
          source: SOURCE_ID,
          'source-layer': 'townships',
          minzoom: 7,
          paint: {
            'line-color': '#6A1B9A',
            'line-width': ['interpolate', ['linear'], ['zoom'], 7, 1, 10, 2, 12, 2.5],
            'line-opacity': this._opacity,
          },
        },
        'plss'
      )
    }

    // Section boundaries - visible at higher zooms
    if (!this.map.getLayer(SECTION_LINE_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: SECTION_LINE_LAYER,
          type: 'line',
          source: SOURCE_ID,
          'source-layer': 'sections',
          minzoom: 9,
          paint: {
            'line-color': '#8E24AA',
            'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.5, 12, 1.2],
            'line-opacity': this._opacity * 0.8,
          },
        },
        'plss'
      )
    }

    // Township labels
    if (!this.map.getLayer(TOWNSHIP_LABEL_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: TOWNSHIP_LABEL_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          'source-layer': 'townships',
          minzoom: 8,
          maxzoom: 11,
          layout: {
            'text-field': ['get', 'PLSSID'],
            'text-font': ['Noto Sans Bold'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 8, 10, 10, 13],
            'text-allow-overlap': false,
            'text-padding': 5,
          },
          paint: {
            'text-color': '#6A1B9A',
            'text-opacity': this._opacity,
            'text-halo-color': 'rgba(255, 255, 255, 0.9)',
            'text-halo-width': 1.5,
          },
        },
        'plss'
      )
    }

    // Section labels (number within township)
    if (!this.map.getLayer(SECTION_LABEL_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: SECTION_LABEL_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          'source-layer': 'sections',
          minzoom: 11,
          layout: {
            'text-field': ['get', 'FRSTDIVNO'],
            'text-font': ['Noto Sans Regular'],
            'text-size': 11,
            'text-allow-overlap': false,
            'text-padding': 3,
          },
          paint: {
            'text-color': '#8E24AA',
            'text-opacity': this._opacity * 0.8,
            'text-halo-color': 'rgba(255, 255, 255, 0.85)',
            'text-halo-width': 1,
          },
        },
        'plss'
      )
    }
  }

  disable(): void {
    if (!this.map) return
    this._enabled = false
    const layers = [
      SECTION_LABEL_LAYER,
      TOWNSHIP_LABEL_LAYER,
      SECTION_LINE_LAYER,
      TOWNSHIP_LINE_LAYER,
      TOWNSHIP_FILL_LAYER,
    ]
    for (const id of layers) {
      if (this.map.getLayer(id)) this.map.removeLayer(id)
    }
    if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID)
  }

  toggle(): void {
    if (this._enabled) this.disable()
    else this.enable()
  }

  setOpacity(opacity: number): void {
    this._opacity = Math.max(0, Math.min(1, opacity))
    if (!this.map) return
    const layerOpacities: [string, string, number][] = [
      [TOWNSHIP_LINE_LAYER, 'line-opacity', this._opacity],
      [SECTION_LINE_LAYER, 'line-opacity', this._opacity * 0.8],
      [TOWNSHIP_LABEL_LAYER, 'text-opacity', this._opacity],
      [SECTION_LABEL_LAYER, 'text-opacity', this._opacity * 0.8],
    ]
    for (const [layerId, prop, val] of layerOpacities) {
      if (this.map.getLayer(layerId)) this.map.setPaintProperty(layerId, prop, val)
    }
  }

  reAdd(): void {
    if (!this._enabled || !this.map) return
    const wasEnabled = this._enabled
    this.disable()
    if (wasEnabled) {
      this._enabled = false
      this.enable()
    }
  }

  queryFeatures(point: maplibregl.Point): maplibregl.MapGeoJSONFeature[] {
    if (!this.map) return []
    const layers: string[] = []
    if (this._enabled) {
      if (this.map.getLayer(SECTION_LINE_LAYER)) layers.push(SECTION_LINE_LAYER)
      if (this.map.getLayer(TOWNSHIP_LINE_LAYER)) layers.push(TOWNSHIP_LINE_LAYER)
    }
    if (layers.length === 0) return []
    return this.map.queryRenderedFeatures(point, { layers })
  }

  buildPopupHtml(features: maplibregl.MapGeoJSONFeature[]): string | null {
    if (features.length === 0) return null
    const parts: string[] = []
    const seen = new Set<string>()

    for (const f of features) {
      const p = f.properties
      const id = p.PLSSID || p.FRSTDIVID || ''
      if (seen.has(id)) continue
      seen.add(id)

      if (f.layer.id === TOWNSHIP_LINE_LAYER) {
        parts.push(
          `<div style="margin-bottom:4px"><strong>Township</strong>: ${p.PLSSID || 'Unknown'}</div>`
        )
      } else if (f.layer.id === SECTION_LINE_LAYER) {
        const section = p.FRSTDIVNO || p.FRSTDIVLAB || 'Unknown'
        parts.push(
          `<div style="margin-bottom:4px"><strong>Section ${section}</strong>${p.FRSTDIVID ? `<br><small>${p.FRSTDIVID}</small>` : ''}</div>`
        )
      }
    }

    return parts.length > 0 ? `<div class="cairn-plss-popup">${parts.join('')}</div>` : null
  }

  private ensureGlyphs(): void {
    if (!this.map) return
    const style = this.map.getStyle()
    if (!style.glyphs) {
      style.glyphs = CACHED_GLYPHS
      this.map.setStyle(style, { diff: true })
    }
  }
}

export const plssService = new PlssService()
